import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Rocket,
  Crown,
  ShieldCheck,
  Globe2,
  BrainCircuit,
  Sun,
  GraduationCap,
  BadgeCheck,
  MapPin,
  Download,
  Share2,
  Radio,
  Check,
  Youtube,
  Instagram,
  Linkedin,
} from "lucide-react";
import { IDEVRA } from "@/data/idevra";
import { sfx } from "@/lib/sound";

const BADGE_ICONS = { Rocket, Crown, ShieldCheck, Globe2, BrainCircuit, Sun, GraduationCap, BadgeCheck };

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const rise = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
};

const Label = ({ children }) => (
  <div className="f-mono text-[9px] tracking-[0.3em] text-[#0284C7]">{children}</div>
);

export default function FounderPanel({ focusIdx, onOpenComms }) {
  const f = IDEVRA.founder;
  const [copied, setCopied] = useState(false);

  const shareProfile = async () => {
    sfx.clank();
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/api/share/founder`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      // clipboard unavailable
    }
  };

  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-6">
      <motion.div variants={rise} className="iv-eyebrow">— PLAYER ONE · FOUNDER PROFILE</motion.div>

      <div className="grid gap-6 lg:grid-cols-[330px_minmax(0,1fr)]">
        {/* Character card */}
        <motion.div variants={rise} className="iv-glass flex flex-col gap-5 rounded-2xl p-6" data-testid="founder-card">
          <div className="flex items-center gap-5">
            <div className="iv-founder-avatar">
              <span className="f-orbitron">SA</span>
              <span className="iv-founder-ring" />
            </div>
            <div>
              <h2 className="f-orbitron text-lg font-black leading-tight text-[#0F172A]">{f.name}</h2>
              <div className="f-mono mt-1 text-[8px] tracking-[0.22em] text-[#0284C7]">{f.class}</div>
            </div>
          </div>

          <div data-testid="founder-level">
            <div className="flex items-baseline justify-between">
              <span className="f-mono text-[9px] tracking-[0.3em] text-[#94A3B8]">LEVEL</span>
              <span className="f-orbitron text-3xl font-black text-[#0284C7]">{f.level}</span>
            </div>
            <div className="iv-stat-track mt-2">
              <motion.div
                className="iv-stat-fill"
                initial={{ width: 0 }}
                animate={{ width: "88%" }}
                transition={{ duration: 1.2, delay: 0.4, ease: "easeOut" }}
              />
            </div>
            <div className="f-mono mt-1.5 text-[8px] tracking-[0.22em] text-[#94A3B8]">{f.levelLabel}</div>
          </div>

          <div className="flex flex-col gap-2">
            {f.titles.map((t) => (
              <span key={t} className="iv-chip !text-[9px]">{t}</span>
            ))}
          </div>

          <div className="flex flex-col gap-1.5">
            {f.bases.map((b) => (
              <span key={b} className="flex items-center gap-2 f-mono text-[9px] tracking-[0.2em] text-[#64748B]">
                <MapPin size={11} color="#0284C7" /> {b}
              </span>
            ))}
          </div>

          <div>
            <Label>— ENTERPRISE CAMPAIGNS</Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {f.enterprises.map((e) => (
                <span key={e} className="iv-chip !px-2.5 !py-1 !text-[8px]">{e}</span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Right column */}
        <div className="flex min-w-0 flex-col gap-6">
          <motion.p variants={rise} className="max-w-3xl text-sm leading-relaxed text-[#475569]">
            {f.bio}
          </motion.p>

          <motion.div variants={rise} className="flex w-full max-w-full flex-wrap items-center gap-3">
            <a
              href="/founder-cv.pdf"
              download="Syed-Amer-Baqer-Executive-CV.pdf"
              data-testid="founder-cv-download-btn"
              onClick={() => sfx.clank()}
              className="f-syncopate inline-flex items-center gap-2 rounded-full bg-[#0284C7] px-5 py-2.5 text-[9px] tracking-[0.25em] text-white shadow-[0_0_24px_rgba(56,189,248,0.5)] transition-colors hover:bg-[#0369A1]"
            >
              DOWNLOAD CV <Download size={12} />
            </a>
            <button
              type="button"
              data-testid="founder-share-btn"
              onClick={shareProfile}
              className={`f-syncopate inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-[9px] tracking-[0.25em] transition-colors ${
                copied
                  ? "border-[#0284C7] text-[#0284C7]"
                  : "border-[#CBD5E1] text-[#475569] hover:border-[#38BDF8] hover:text-[#0284C7]"
              }`}
            >
              {copied ? (
                <>
                  COPIED <Check size={12} />
                </>
              ) : (
                <>
                  SHARE PROFILE <Share2 size={12} />
                </>
              )}
            </button>
            <a
              href="https://www.linkedin.com/in/syed-amer/"
              target="_blank"
              rel="noreferrer"
              data-testid="founder-linkedin-btn"
              onClick={() => sfx.clank()}
              className="f-syncopate inline-flex items-center gap-2 rounded-full border border-[#CBD5E1] px-5 py-2.5 text-[9px] tracking-[0.25em] text-[#475569] transition-colors hover:border-[#38BDF8] hover:text-[#0284C7]"
            >
              LINKEDIN <Linkedin size={12} />
            </a>
            <button
              type="button"
              data-testid="founder-comms-btn"
              onClick={onOpenComms}
              className="f-syncopate inline-flex items-center gap-2 rounded-full border border-[#CBD5E1] px-5 py-2.5 text-[9px] tracking-[0.25em] text-[#475569] transition-colors hover:border-[#38BDF8] hover:text-[#0284C7]"
            >
              OPEN COMMS <Radio size={12} />
            </button>
          </motion.div>

          <motion.div variants={rise} data-testid="founder-attributes">
            <Label>— ATTRIBUTES</Label>
            <div className="mt-3 flex max-w-xl flex-col gap-2.5">
              {f.attributes.map((s, i) => (
                <div key={s.label} className="flex items-center gap-3">
                  <span className="f-mono w-32 shrink-0 text-[9px] tracking-[0.18em] text-[#64748B]">{s.label}</span>
                  <div className="iv-stat-track flex-1">
                    <motion.div
                      className="iv-stat-fill"
                      initial={{ width: 0 }}
                      animate={{ width: `${s.value}%` }}
                      transition={{ duration: 0.9, delay: 0.3 + i * 0.1, ease: "easeOut" }}
                    />
                  </div>
                  <span className="f-orbitron w-8 text-right text-xs font-bold text-[#0284C7]">{s.value}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={rise} data-testid="founder-achievements">
            <Label>— ACHIEVEMENTS UNLOCKED</Label>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {f.achievements.map((a) => {
                const Icon = BADGE_ICONS[a.icon];
                return (
                  <div key={a.name} className="iv-glass rounded-xl p-3.5 transition-shadow duration-300 hover:shadow-[0_0_0_1px_rgba(56,189,248,0.5),0_0_20px_rgba(56,189,248,0.35)]">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0284C7]/10">
                      <Icon size={15} color="#0284C7" />
                    </span>
                    <div className="f-orbitron mt-2.5 text-[11px] font-bold text-[#0F172A]">{a.name}</div>
                    <div className="mt-1 text-[10px] leading-snug text-[#64748B]">{a.desc}</div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Quest log */}
      <motion.div variants={rise} data-testid="founder-quests">
        <Label>— CAREER QUEST LOG · 2005 → NOW</Label>
        <div className="iv-glass mt-3 overflow-hidden rounded-2xl">
          {f.quests.map((q, i) => (
            <div
              key={`${q.org}-${q.years}`}
              data-testid={`founder-quest-${i}`}
              className={`grid grid-cols-[6.5rem_1fr] items-center gap-3 border-b border-white/60 px-4 py-2.5 last:border-b-0 sm:grid-cols-[8rem_1fr_auto] sm:px-6 ${
                i === focusIdx ? "bg-white/85 shadow-[inset_0_0_0_2px_rgba(56,189,248,0.5)]" : ""
              }`}
            >
              <span className="f-mono text-[9px] tracking-[0.12em] text-[#94A3B8]">{q.years}</span>
              <span className="min-w-0">
                <span className="flex items-center gap-2">
                  <span className="block f-orbitron text-[12px] font-bold text-[#0F172A]">{q.org}</span>
                  {q.link && (
                    <a
                      href={q.link}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`timeline-yt-${q.years.slice(0, 4)}`}
                      title={`${q.org} on YouTube`}
                      onClick={(e) => {
                        e.stopPropagation();
                        sfx.tick();
                      }}
                      className="text-[#94A3B8] transition-colors hover:text-[#0284C7]"
                    >
                      <Youtube size={13} />
                    </a>
                  )}
                  {q.insta && (
                    <a
                      href={q.insta}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`timeline-ig-${q.years.slice(0, 4)}`}
                      title={`${q.org} on Instagram`}
                      onClick={(e) => {
                        e.stopPropagation();
                        sfx.tick();
                      }}
                      className="text-[#94A3B8] transition-colors hover:text-[#0284C7]"
                    >
                      <Instagram size={12} />
                    </a>
                  )}
                </span>
                <span className="block truncate text-[10px] text-[#64748B]">{q.role}</span>
              </span>
              <span className={`hidden f-mono text-[9px] tracking-[0.2em] sm:block ${q.active ? "text-[#0284C7]" : "text-[#94A3B8]"}`}>
                {q.active ? "► ACTIVE QUEST" : "✔ COMPLETE"}
              </span>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div variants={rise} className="flex flex-wrap items-center gap-3" data-testid="founder-education">
        <span className="f-mono text-[9px] tracking-[0.25em] text-[#64748B]">EDUCATION &amp; TRAINING:</span>
        {f.education.map((e) => (
          <span key={e} className="iv-chip !py-1.5 !text-[9px]">{e}</span>
        ))}
      </motion.div>
    </motion.div>
  );
}
